import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, Trash, PencilSimple, Copy, Check, Play, ArrowClockwise,
  LinkSimple, Clock, DownloadSimple, Eye, EyeSlash, ArrowLineDown, ListMagnifyingGlass,
} from "@phosphor-icons/react";
import { SOURCES, STATUSES } from "../lib/constants";
import useLeadSources from "../lib/useLeadSources";

// ------- Modal shell -------
function Modal({ open, onClose, title, children, width = "max-w-3xl" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className={`bg-white border border-border ${width} w-full max-h-[92vh] overflow-hidden flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold tracking-tight">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function IconBtn({ onClick, children, title, danger, testid }) {
  return (
    <button
      onClick={onClick} title={title} data-testid={testid}
      className={`w-8 h-8 flex items-center justify-center border border-border hover:bg-secondary transition-colors ${danger ? "hover:border-[#E00000] hover:text-[#E00000]" : ""}`}
    >
      {children}
    </button>
  );
}

function CopyBtn({ text, testid }) {
  const [c, setC] = useState(false);
  return (
    <button
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
        setC(true); setTimeout(() => setC(false), 1200);
      }}
      title="Copy" data-testid={testid}
      className="p-1.5 border border-border hover:bg-secondary"
    >
      {c ? <Check size={12} className="text-[#008A00]" /> : <Copy size={12} />}
    </button>
  );
}

// ------- Aliases editor (per target field, multiple aliases) -------
function AliasesEditor({ mapping, onChange }) {
  const rows = Object.entries(mapping || {});
  const addTarget = () => onChange({ ...mapping, "": [] });
  const removeTarget = (targetKey) => {
    const next = { ...mapping };
    delete next[targetKey];
    onChange(next);
  };
  const renameTarget = (oldKey, newKey) => {
    const next = {};
    for (const [k, v] of Object.entries(mapping)) next[k === oldKey ? newKey : k] = v;
    onChange(next);
  };
  const setAliases = (targetKey, aliasCsv) => {
    onChange({ ...mapping, [targetKey]: aliasCsv.split(",").map((s) => s.trim()).filter(Boolean) });
  };
  return (
    <div className="border border-border">
      <div className="px-4 py-2 bg-secondary border-b border-border grid grid-cols-[200px_1fr_36px] gap-2 text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">
        <span>Target field</span>
        <span>Payload keys (comma-separated aliases)</span>
        <span />
      </div>
      <div className="max-h-72 overflow-y-auto divide-y divide-border">
        {rows.length === 0 && (
          <div className="px-4 py-6 text-xs text-muted-foreground text-center">No fields. Click &quot;+ Add target&quot;.</div>
        )}
        {rows.map(([target, aliases], idx) => (
          <div key={idx} className="px-2 py-1.5 grid grid-cols-[200px_1fr_36px] gap-2 items-start">
            <input
              value={target}
              onChange={(e) => renameTarget(target, e.target.value)}
              placeholder="phone / customer_name…"
              className="text-xs font-mono border border-border px-2 py-1.5 focus:border-primary focus:outline-none"
            />
            <input
              value={(aliases || []).join(", ")}
              onChange={(e) => setAliases(target, e.target.value)}
              placeholder="mobile, Mobile, phone_number, Contact"
              className="text-xs font-mono border border-border px-2 py-1.5 focus:border-primary focus:outline-none"
            />
            <IconBtn onClick={() => removeTarget(target)} title="Remove" danger><Trash size={14} /></IconBtn>
          </div>
        ))}
      </div>
      <div className="px-4 py-2 border-t border-border">
        <button onClick={addTarget} className="text-[10px] uppercase tracking-[0.15em] font-bold text-primary hover:underline flex items-center gap-1">
          <Plus size={12} /> Add target
        </button>
      </div>
    </div>
  );
}

// ------- Create / Edit modal -------
// ------- Source picker with inline "+ Add new" -------
function SourcePicker({ value, onChange, sources, onAdd }) {
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(false);

  if (adding) {
    return (
      <div className="mt-1 flex items-stretch gap-1" data-testid="source-picker-add">
        <input
          autoFocus
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape") { setAdding(false); setNewLabel(""); } }}
          placeholder="e.g. Diwali 2026 Landing"
          className="flex-1 border border-primary px-3 py-2 text-sm focus:outline-none"
          data-testid="new-source-label-input"
        />
        <button
          type="button"
          disabled={busy || !newLabel.trim()}
          onClick={async () => {
            setBusy(true);
            try { await onAdd(newLabel.trim()); setAdding(false); setNewLabel(""); }
            finally { setBusy(false); }
          }}
          className="px-3 text-[10px] uppercase tracking-[0.15em] font-bold bg-primary text-primary-foreground disabled:opacity-50"
          data-testid="new-source-save-btn"
        >
          {busy ? "Saving…" : "Add"}
        </button>
        <button
          type="button"
          onClick={() => { setAdding(false); setNewLabel(""); }}
          className="px-3 text-[10px] uppercase tracking-[0.15em] font-bold border border-border hover:bg-secondary"
        >
          Cancel
        </button>
      </div>
    );
  }
  return (
    <select
      value={value}
      onChange={(e) => {
        if (e.target.value === "__add_new__") { setAdding(true); return; }
        onChange(e.target.value);
      }}
      className="w-full mt-1 border border-border px-3 py-2 text-sm bg-white focus:border-primary focus:outline-none"
      data-testid="source-picker-select"
    >
      {/* Existing source might be a legacy value not in the current list */}
      {value && !sources.find((s) => s.id === value) && (
        <option value={value}>{value} (unlisted)</option>
      )}
      {sources.map((s) => (
        <option key={s.id} value={s.id}>
          {s.label}{s.builtin === false ? " ✦" : ""}
        </option>
      ))}
      <option value="__add_new__">+ Add new source…</option>
    </select>
  );
}


function WebhookModal({ open, onClose, editing, branches, agents, defaultMapping, onSaved }) {
  const { sources: dynSources, addSource } = useLeadSources();
  const sourceList = dynSources.length ? dynSources : SOURCES;
  // Default source is the FIRST canonical source, not "other" — otherwise the
  // dropdown displays a value that doesn't match the underlying state and every
  // new webhook silently saves source="other".
  const DEFAULT_SOURCE = sourceList[0]?.id || "google_ads";
  const [form, setForm] = useState({
    name: "", default_source: DEFAULT_SOURCE, default_status: "new",
    default_branch_id: "", default_agent_id: "", active: true,
    field_mapping: {},
  });
  const [testSample, setTestSample] = useState(
    JSON.stringify({
      full_name: "Sample Lead",
      mobile: "9999999999",
      email: "sample@example.com",
      message: "test enquiry from landing page",
      utm_source: "google",
      utm_medium: "cpc",
      utm_campaign: "summer_sale_2026",
      utm_content: "hero_cta",
      utm_term: "hair transplant",
      gclid: "TeSter123GCLID",
      fbclid: "",
      ad_id: "1234567890",
      adset_id: "9876543210",
      landing_page: "https://example.com/lp/summer",
      referrer: "https://www.google.com/",
    }, null, 2),
  );
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      const src = editing || {
        name: "", default_source: DEFAULT_SOURCE, default_status: "new",
        default_branch_id: "", default_agent_id: "", active: true,
        field_mapping: defaultMapping || {},
      };
      // If we're editing an existing hook whose stored source isn't in the
      // known SOURCES list (e.g. legacy "other"), keep it as-is — the combobox
      // will allow the admin to reassign or leave it.
      const knownSourceIds = new Set(sourceList.map((s) => s.id));
      const existingSource = src.default_source || DEFAULT_SOURCE;
      const normalizedSource = knownSourceIds.has(existingSource) ? existingSource : (existingSource || DEFAULT_SOURCE);
      setForm({
        name: src.name || "",
        default_source: normalizedSource,
        default_status: src.default_status || "new",
        default_branch_id: src.default_branch_id || "",
        default_agent_id: src.default_agent_id || "",
        active: src.active !== false,
        field_mapping: src.field_mapping || defaultMapping || {},
      });
      setTestResult(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, defaultMapping]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      if (editing?.id) {
        await api.patch(`/webhooks-in/${editing.id}`, form);
        toast.success("Webhook updated");
      } else {
        const { data } = await api.post("/webhooks-in", form);
        toast.success(`Webhook "${data.name}" created`);
      }
      onSaved?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const runTest = async () => {
    if (!editing?.id) { toast.error("Save the webhook first to test"); return; }
    try {
      const parsed = JSON.parse(testSample);
      const { data } = await api.post(`/webhooks-in/${editing.id}/test`, parsed);
      setTestResult(data);
    } catch (e) {
      if (e.message?.includes("JSON")) toast.error("Invalid JSON sample");
      else toast.error(formatApiError(e));
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? `Edit · ${editing.name}` : "New inbound webhook"} width="max-w-4xl">
      <div className="p-6 space-y-6">
        {/* Basics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Name</label>
            <input
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Meta India · Homepage Form"
              data-testid="in-webhook-name"
              className="w-full mt-1 border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Default source</label>
            <SourcePicker
              value={form.default_source}
              onChange={(v) => setForm({ ...form, default_source: v })}
              sources={sourceList}
              onAdd={async (label) => {
                try {
                  const created = await addSource(label);
                  setForm((f) => ({ ...f, default_source: created.id }));
                  toast.success(`Added source "${created.label}"`);
                } catch (e) { toast.error(formatApiError(e)); }
              }}
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Default status</label>
            <select
              value={form.default_status}
              onChange={(e) => setForm({ ...form, default_status: e.target.value })}
              className="w-full mt-1 border border-border px-3 py-2 text-sm bg-white focus:border-primary focus:outline-none"
            >
              {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Default branch (blank = auto-route)</label>
            <select
              value={form.default_branch_id}
              onChange={(e) => setForm({ ...form, default_branch_id: e.target.value })}
              className="w-full mt-1 border border-border px-3 py-2 text-sm bg-white focus:border-primary focus:outline-none"
            >
              <option value="">(auto-route by pincode/city)</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-primary" />
          Active (accept incoming requests)
        </label>

        {/* Field mapping */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
              Field mapping <span className="normal-case tracking-normal text-muted-foreground">— each target field lists the payload keys to look for</span>
            </label>
            <button
              type="button"
              onClick={() => {
                const merged = { ...(defaultMapping || {}), ...form.field_mapping };
                // Ensure any missing default targets are added with their default aliases
                for (const [k, v] of Object.entries(defaultMapping || {})) {
                  if (!(k in form.field_mapping)) merged[k] = v;
                }
                setForm({ ...form, field_mapping: merged });
                toast.success("Added missing attribution defaults (utm_*, gclid, fbclid, ad_id, …)");
              }}
              data-testid="merge-attribution-defaults"
              className="px-2 py-1 border border-border hover:bg-secondary text-[10px] uppercase tracking-[0.15em] font-bold flex items-center gap-1"
              title="Add utm_*, gclid, fbclid, ad_id, adset_id, landing_page, referrer targets if missing"
            >
              <Plus size={10} /> Add attribution defaults
            </button>
          </div>
          <AliasesEditor
            mapping={form.field_mapping}
            onChange={(m) => setForm({ ...form, field_mapping: m })}
          />
          <div className="mt-2 text-[11px] text-muted-foreground leading-relaxed">
            <span className="font-semibold text-foreground">Supported attribution targets:</span>{" "}
            <code className="text-[10px] font-mono">utm_source</code>,{" "}
            <code className="text-[10px] font-mono">utm_medium</code>,{" "}
            <code className="text-[10px] font-mono">utm_campaign</code>,{" "}
            <code className="text-[10px] font-mono">utm_content</code>,{" "}
            <code className="text-[10px] font-mono">utm_term</code>,{" "}
            <code className="text-[10px] font-mono">gclid</code>,{" "}
            <code className="text-[10px] font-mono">fbclid</code>,{" "}
            <code className="text-[10px] font-mono">ad_id</code>,{" "}
            <code className="text-[10px] font-mono">adset_id</code>,{" "}
            <code className="text-[10px] font-mono">landing_page</code>,{" "}
            <code className="text-[10px] font-mono">referrer</code>,{" "}
            <code className="text-[10px] font-mono">campaign_id</code>.
            {" "}These lift to top-level lead fields for filters, exports and the Attribution Health widget.
          </div>
        </div>

        {/* Test panel (only when editing) */}
        {editing?.id && (
          <details open className="border border-border">
            <summary className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground bg-secondary cursor-pointer">
              Test with sample payload (dry-run)
            </summary>
            <div className="p-4 space-y-3">
              <textarea
                value={testSample} onChange={(e) => setTestSample(e.target.value)} rows={5}
                className="w-full border border-border px-3 py-2 text-xs font-mono focus:border-primary focus:outline-none"
              />
              <button onClick={runTest} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold border border-border hover:bg-secondary flex items-center gap-1.5">
                <Play size={12} /> Run dry-run
              </button>
              {testResult && (
                <div className="border border-border bg-secondary/40">
                  <div className="px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border-b border-border">
                    {testResult.ok ? "✓ Would create this lead" : `✗ ${testResult.error}`}
                  </div>
                  <pre className="px-4 py-3 text-[11px] font-mono overflow-x-auto max-h-64 overflow-y-auto">
                    {JSON.stringify(testResult.would_create || testResult.parsed, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </details>
        )}
      </div>

      <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-border hover:bg-secondary">Cancel</button>
        <button onClick={save} disabled={busy} data-testid="in-webhook-save"
          className="px-5 py-2 text-xs uppercase tracking-[0.15em] font-bold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
          {busy ? "Saving…" : editing ? "Save changes" : "Create webhook"}
        </button>
      </div>
    </Modal>
  );
}

// ------- URL + token viewer -------
function CredentialsCard({ hook }) {
  const [show, setShow] = useState(false);
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(false);

  const reveal = async () => {
    if (full) { setShow(true); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/webhooks-in/${hook.id}?reveal_token=true`);
      setFull(data);
      setShow(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  // Build URL using the current browser origin (matches whatever domain the
  // user is looking at — production or preview). Only reveal the token after
  // the admin clicks "Show token".
  const origin = window.location.origin;
  const maskedUrl = `${origin}/api/w/${hook.slug}?token=${hook.token_masked || "•••"}`;
  const fullUrl = full ? `${origin}/api/w/${hook.slug}?token=${full.token || ""}` : maskedUrl;
  const url = show ? fullUrl : maskedUrl;

  const copyFull = async () => {
    if (!full) await reveal();
    const t = (full && full.token) || (await api.get(`/webhooks-in/${hook.id}?reveal_token=true`)).data.token;
    const finalUrl = `${origin}/api/w/${hook.slug}?token=${t}`;
    try { await navigator.clipboard.writeText(finalUrl); toast.success("Full URL copied — paste into your source tool"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="border border-border">
      <div className="px-3 py-2 border-b border-border flex items-center justify-between bg-secondary/30">
        <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Public URL</span>
        <div className="flex items-center gap-2">
          <button
            onClick={show ? () => setShow(false) : reveal}
            data-testid={`reveal-token-${hook.id}`}
            disabled={loading}
            className="inline-flex items-center gap-1 px-2 py-1 border border-border hover:bg-secondary text-[10px] uppercase tracking-[0.15em] font-bold disabled:opacity-50"
            title={show ? "Hide token" : "Reveal the full webhook token"}
          >
            {show ? <EyeSlash size={12} /> : <Eye size={12} />}
            {loading ? "…" : (show ? "Hide" : "Show token")}
          </button>
          <button
            onClick={copyFull}
            data-testid={`copy-full-url-${hook.id}`}
            className="inline-flex items-center gap-1 px-2 py-1 border border-primary bg-primary text-primary-foreground hover:bg-primary/90 text-[10px] uppercase tracking-[0.15em] font-bold"
            title="Copy the full URL with real token"
          >
            <Copy size={12} /> Copy full URL
          </button>
        </div>
      </div>
      <code className="px-3 py-2 block font-mono text-[11px] break-all" data-testid={`webhook-url-${hook.id}`}>{url}</code>
      {show && (
        <div className="px-3 py-1.5 border-t border-border bg-amber-50 text-[10px] text-amber-900 flex items-center gap-1.5">
          <Eye size={11} /> Token revealed — anyone with this URL can post leads. Rotate if it leaks.
        </div>
      )}
    </div>
  );
}

// ------- Recent hits drawer -------
function HitsModal({ open, onClose, hook }) {
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const reload = async () => {
    if (!hook?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/webhooks-in/${hook.id}/hits?limit=50`);
      setHits(data.hits || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { if (open) reload(); }, [open, hook?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal open={open} onClose={onClose} title={`Recent hits · ${hook?.name || ""}`} width="max-w-4xl">
      <div className="px-6 py-3 border-b border-border flex items-center justify-between bg-secondary/40">
        <div className="text-[11px] text-muted-foreground">
          Every incoming request (success or failure) with full raw + flattened + parsed payload. Newest first.
        </div>
        <button onClick={reload} disabled={loading} data-testid="hits-refresh"
          className="px-2 py-1 border border-border hover:bg-secondary text-[10px] uppercase tracking-[0.15em] font-bold flex items-center gap-1 disabled:opacity-50">
          <ArrowClockwise size={11} /> {loading ? "Loading…" : "Refresh"}
        </button>
      </div>
      <div className="divide-y divide-border">
        {!loading && hits.length === 0 && (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground">
            No hits recorded yet. Fire a test payload from your form or the &quot;Test dry-run&quot; box.
          </div>
        )}
        {hits.map((h, idx) => {
          const isOk = h.ok;
          const status = h.response_status || (isOk ? 200 : "?");
          const isExp = expanded === idx;
          return (
            <div key={idx} data-testid={`hit-row-${idx}`}>
              <button
                onClick={() => setExpanded(isExp ? null : idx)}
                className="w-full text-left px-6 py-3 flex items-center gap-3 hover:bg-secondary/40"
              >
                <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-bold border ${isOk ? "border-[#008A00] text-[#008A00] bg-[#008A00]/5" : "border-[#E00000] text-[#E00000] bg-[#E00000]/5"}`}>
                  {status}
                </span>
                <span className="text-[11px] text-muted-foreground shrink-0">
                  {new Date(h.at).toLocaleString()}
                </span>
                <span className="text-[11px] font-mono truncate flex-1">
                  {isOk
                    ? <>lead=<span className="text-primary">{h.lead_id?.slice(0, 8)}…</span></>
                    : <>error=<span className="text-[#E00000]">{h.error}</span></>}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {isExp ? "Hide" : "Show payload"}
                </span>
              </button>
              {isExp && (
                <div className="px-6 pb-4 pt-2 bg-secondary/20 grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <HitPre title="Raw payload" data={h.payload} />
                  <HitPre title="Flattened" data={h.flattened} />
                  <HitPre title="Parsed (via mapping)" data={h.parsed} />
                  {h.raw_body_preview && (
                    <div className="lg:col-span-3">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1">Raw request body (first 2KB)</div>
                      <pre className="p-2 text-[10px] font-mono bg-white border border-border overflow-x-auto max-h-40 overflow-y-auto whitespace-pre-wrap break-all">{h.raw_body_preview}</pre>
                    </div>
                  )}
                  <div className="lg:col-span-3 grid grid-cols-2 gap-4 text-[11px]">
                    <div><span className="text-muted-foreground">Content-Type:</span> <span className="font-mono">{h.content_type || "—"}</span></div>
                    <div><span className="text-muted-foreground">User-Agent:</span> <span className="font-mono truncate">{h.user_agent || "—"}</span></div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

function HitPre({ title, data }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1">{title}</div>
      <pre className="p-2 text-[10px] font-mono bg-white border border-border overflow-x-auto max-h-56 overflow-y-auto">
        {JSON.stringify(data || {}, null, 2)}
      </pre>
    </div>
  );
}

// ------- Main component -------
export default function InboundWebhooks({ branches = [], agents = [] }) {
  const { sources: allSources } = useLeadSources();
  const sourceById = React.useMemo(
    () => Object.fromEntries((allSources || []).map((s) => [s.id, s])),
    [allSources]
  );
  const [hooks, setHooks] = useState([]);
  const [defaultMapping, setDefaultMapping] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [hitsHook, setHitsHook] = useState(null);

  const load = async () => {
    try {
      const [list, defaults] = await Promise.all([
        api.get("/webhooks-in"),
        api.get("/webhooks-in/defaults"),
      ]);
      setHooks(list.data || []);
      setDefaultMapping(defaults.data.field_mapping || {});
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (h) => {
    try {
      await api.patch(`/webhooks-in/${h.id}`, { active: !h.active });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rotateToken = async (h) => {
    if (!confirm(`Rotate token for "${h.name}"? Existing integrations will break until you update them with the new token.`)) return;
    try {
      const { data } = await api.post(`/webhooks-in/${h.id}/rotate-token`);
      toast.success("Token rotated");
      // Show the new token via URL copy
      const newUrl = `${window.location.origin}/api/w/${h.slug}?token=${data.token}`;
      try { await navigator.clipboard.writeText(newUrl); } catch { /* ignore */ }
      toast.message("New URL copied to clipboard");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (h) => {
    if (!confirm(`Delete "${h.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/webhooks-in/${h.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const backfillSource = async (h) => {
    // Dry-run first so admin sees the count
    let dry;
    try {
      const { data } = await api.post(`/webhooks-in/${h.id}/backfill-source?dry_run=true`);
      dry = data;
    } catch (e) { toast.error(formatApiError(e)); return; }
    if (!dry.candidate_count) {
      toast.info(`No leads to re-source for "${h.name}". Everything already has a valid source.`);
      return;
    }
    if (!confirm(
      `Update ${dry.candidate_count} existing lead(s) that came in through "${h.name}" to source = "${dry.target_source}"?\n\n` +
      `This cannot be undone.`
    )) return;
    try {
      const { data } = await api.post(`/webhooks-in/${h.id}/backfill-source?dry_run=false`);
      toast.success(`Re-sourced ${data.applied} lead(s) to "${data.target_source}"`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const backfillAttribution = async () => {
    let dry;
    try {
      const { data } = await api.post(`/webhooks-in/backfill-attribution?dry_run=true`);
      dry = data;
    } catch (e) { toast.error(formatApiError(e)); return; }
    if (!dry.would_update) {
      toast.info(`No existing leads need attribution backfill (scanned ${dry.scanned}).`);
      return;
    }
    if (!confirm(
      `Backfill attribution on ${dry.would_update} lead(s)?\n\n` +
      `This lifts utm_*, gclid, fbclid, ad_id, adset_id, landing_page, referrer, campaign_id ` +
      `from metadata.raw_payload to top-level lead fields. Safe: never overwrites existing values.`
    )) return;
    try {
      const { data } = await api.post(`/webhooks-in/backfill-attribution?dry_run=false`);
      toast.success(`Backfilled attribution on ${data.updated} lead(s)`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="border border-border bg-white" data-testid="inbound-webhooks-section">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Incoming · Custom endpoints for 3rd-party tools</div>
          <h3 className="font-display text-lg font-semibold tracking-tight flex items-center gap-2 mt-0.5">
            <ArrowLineDown size={16} weight="fill" className="text-primary" />
            Custom inbound webhooks
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={backfillAttribution}
            data-testid="backfill-attribution-btn"
            title="Retro-fit UTM / click-ID / ad metadata onto existing leads from their raw webhook payload"
            className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border border-border hover:bg-secondary flex items-center gap-1.5"
          >
            <ArrowClockwise size={12} /> Backfill attribution
          </button>
          <button
            onClick={() => { setEditing(null); setModalOpen(true); }}
            data-testid="new-inbound-webhook-button"
            className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1.5">
            <Plus size={14} weight="bold" /> New webhook
          </button>
        </div>
      </div>

      {loading && <div className="px-5 py-8 text-xs text-muted-foreground text-center">Loading…</div>}

      {!loading && hooks.length === 0 && (
        <div className="px-5 py-10 text-center">
          <ArrowLineDown size={32} className="mx-auto text-muted-foreground/50" />
          <p className="text-sm font-semibold mt-3">No custom inbound webhooks yet</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
            Create one per 3rd-party tool sending leads TO this CRM (form builders, ad platforms, IVR). Each gets its own URL + token + field mapping.
          </p>
        </div>
      )}

      {!loading && hooks.length > 0 && (
        <div className="divide-y divide-border">
          {hooks.map((h) => (
            <div key={h.id} className="px-5 py-4" data-testid={`inbound-webhook-${h.id}`}>
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0 space-y-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{h.name}</span>
                    <button
                      onClick={() => toggleActive(h)}
                      className={`text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 border ${h.active ? "border-[#008A00] text-[#008A00] bg-[#008A00]/5" : "border-border text-muted-foreground"}`}>
                      {h.active ? "Active" : "Paused"}
                    </button>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                      {sourceById[h.default_source]?.label || h.default_source || "—"} → {STATUSES.find((s) => s.id === h.default_status)?.label || h.default_status || "—"}
                    </span>
                    {h.default_source && !sourceById[h.default_source] && (
                      <span
                        data-testid={`invalid-source-warning-${h.id}`}
                        title="This webhook has an unrecognised source. Edit it to pick a valid one, then click Repair leads."
                        className="text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 border border-amber-500 text-amber-700 bg-amber-50">
                        Invalid source — fix this
                      </span>
                    )}
                  </div>
                  <CredentialsCard hook={h} />
                  <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                    <Clock size={11} /> {h.hit_count || 0} hits
                    {h.last_hit_at && <span>· last {new Date(h.last_hit_at).toLocaleString()}</span>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0 items-start">
                  <button
                    onClick={() => setHitsHook(h)}
                    data-testid={`view-hits-${h.id}`}
                    title="Show the last 50 requests this webhook received (raw + flattened + parsed)"
                    className="h-8 px-2 flex items-center gap-1 border border-border hover:bg-secondary text-[10px] uppercase tracking-[0.15em] font-bold"
                  >
                    <ListMagnifyingGlass size={11} /> Hits
                  </button>
                  <button
                    onClick={() => backfillSource(h)}
                    data-testid={`backfill-${h.id}`}
                    title="Update existing leads that came through this webhook to use the current source. Safe: only touches leads whose source is invalid."
                    className="h-8 px-2 flex items-center gap-1 border border-border hover:bg-secondary text-[10px] uppercase tracking-[0.15em] font-bold"
                  >
                    <ArrowClockwise size={11} /> Repair
                  </button>
                  <IconBtn onClick={() => rotateToken(h)} title="Rotate token" testid={`rotate-${h.id}`}><ArrowClockwise size={14} /></IconBtn>
                  <IconBtn onClick={() => { setEditing(h); setModalOpen(true); }} title="Edit" testid={`edit-in-${h.id}`}><PencilSimple size={14} /></IconBtn>
                  <IconBtn onClick={() => del(h)} title="Delete" testid={`delete-in-${h.id}`} danger><Trash size={14} /></IconBtn>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <WebhookModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editing={editing}
        branches={branches}
        agents={agents}
        defaultMapping={defaultMapping}
        onSaved={load}
      />
      <HitsModal
        open={!!hitsHook}
        onClose={() => setHitsHook(null)}
        hook={hitsHook}
      />
    </div>
  );
}
